import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { EventWidgetData } from "@/api/bale.zod";

import { Button } from "@/components/Button";
import { Card, CardActionsHeader } from "@/components/Card";
import { Icon } from "@/components/Icon";
import { IconButton } from "@/components/IconButton";
import { MenuItem } from "@/components/MenuItem";

/** Additional widgets are the ones that are not the required default widgets. */
export type AdditionalWidget = Exclude<EventWidgetData["widget_type"], "summary" | "attending" | "discussion">;

type Props = {
  onAdd: (type: AdditionalWidget) => void;
  className?: string;
};

export function AddEventWidget({ onAdd, className }: Props) {
  const { t } = useTranslation();
  const [isChoosing, setIsChoosing] = useState(false);

  function addWidget(widgetType: AdditionalWidget) {
    onAdd(widgetType);
    setIsChoosing(false);
  }

  if (!isChoosing) {
    return (
      <Button type="button" className={className} onClick={() => setIsChoosing(true)}>
        {t("event-add-widget")}
      </Button>
    );
  }

  return (
    <Card className={className}>
      <CardActionsHeader title={t("event-add-widget")}>
        <IconButton type="button" name="x" aria-label={t("cancel")} onClick={() => setIsChoosing(false)} />
      </CardActionsHeader>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col border-t border-divider">
          <MenuItem className="w-full" onClick={() => addWidget("note")}>
            <Icon name="file-text" />
            <div className="flex flex-col">
              <p>{t("event-widget-type-note")}</p>
              <p className="font-hint text-placeholder">{t("event-widget-note-description")}</p>
            </div>
          </MenuItem>
          <MenuItem className="w-full" onClick={() => addWidget("directions")}>
            <Icon name="map-pin" />
            <div className="flex flex-col">
              <p>{t("event-widget-type-directions")}</p>
              <p className="font-hint text-placeholder">{t("event-widget-directions-description")}</p>
            </div>
          </MenuItem>
          <MenuItem className="w-full" onClick={() => addWidget("poll")}>
            <Icon name="bar-chart-2" />
            <div className="flex flex-col">
              <p>{t("event-widget-type-poll")}</p>
              <p className="font-hint text-placeholder">{t("event-widget-poll-description")}</p>
            </div>
          </MenuItem>
          <MenuItem className="w-full" onClick={() => addWidget("poll")} disabled>
            <Icon name="edit-3" />
            <div className="flex flex-col">
              <p>{t("event-widget-type-questionnaire")}</p>
              <p className="font-hint text-placeholder">{t("event-widget-questionnaire-description")}</p>
            </div>
            <div className="ml-auto">{t("feature-coming-soon")}</div>
          </MenuItem>
          <MenuItem className="w-full" onClick={() => addWidget("poll")} disabled>
            <Icon name="image" />
            <div className="flex flex-col">
              <p>{t("event-widget-type-banner")}</p>
              <p className="font-hint text-placeholder">{t("event-widget-banner-description")}</p>
            </div>
            <div className="ml-auto">{t("feature-coming-soon")}</div>
          </MenuItem>
          <MenuItem className="w-full" onClick={() => addWidget("poll")} disabled>
            <Icon name="upload-cloud" />
            <div className="flex flex-col">
              <p>{t("event-widget-type-file-drop")}</p>
              <p className="font-hint text-placeholder">{t("event-widget-file-drop-description")}</p>
            </div>
            <div className="ml-auto">{t("feature-coming-soon")}</div>
          </MenuItem>
          <MenuItem className="w-full" onClick={() => addWidget("poll")} disabled>
            <Icon name="pen-tool" />
            <div className="flex flex-col">
              <p>{t("event-widget-type-whiteboard")}</p>
              <p className="font-hint text-placeholder">{t("event-widget-whiteboard-description")}</p>
            </div>
            <div className="ml-auto">{t("feature-coming-soon")}</div>
          </MenuItem>
        </div>
      </div>
    </Card>
  );
}
